import {
  AuthResponseSchema,
  LoginRequestSchema,
  LogoutResponseSchema,
  RegisterRequestSchema,
  UserDTOSchema,
  type AuthResponse,
  type LoginRequest,
  type RegisterRequest,
  type UserDTO,
} from "@algoquest/contracts";
import { apiRequest, ApiClientError, setAccessToken } from "./httpClient";

let sessionUserId:string|null=null;

/**
 * Phase 10 — thin wrappers over Phase 09's `/api/v1/auth/*` and
 * `/api/v1/users/me`. Every function here that receives an access token
 * stores it via `httpClient.ts`'s `setAccessToken` immediately — callers
 * never handle the token directly, which is what keeps "never
 * localStorage" (§30) enforceable at a single choke point.
 */

/** Parses client-side too (not just server-side) so a malformed form submission fails fast in the UI instead of round-tripping to the API first. */
export async function register(input: RegisterRequest): Promise<AuthResponse> {
  RegisterRequestSchema.parse(input);
  const result = await apiRequest("/auth/register", { method: "POST", body: input }, AuthResponseSchema);
  sessionUserId=result.user.id;
  setAccessToken(result.accessToken);
  return result;
}

export async function login(input: LoginRequest): Promise<AuthResponse> {
  LoginRequestSchema.parse(input);
  const result = await apiRequest("/auth/login", { method: "POST", body: input }, AuthResponseSchema);
  sessionUserId=result.user.id;
  setAccessToken(result.accessToken);
  return result;
}

export async function logout(): Promise<void> {
  if(refreshing)await refreshing;
  await apiRequest("/auth/logout", { method: "POST" }, LogoutResponseSchema);
  setAccessToken(null);
  sessionUserId=null;
}

/**
 * Uses the httpOnly refresh cookie (sent automatically via
 * `credentials: "include"`, see `httpClient.ts`) to mint a fresh access
 * token — no request body needed, matching the `/auth/refresh` contract
 * exactly. Returns `null` (rather than throwing) on any failure, since
 * "no valid session yet" is an expected outcome at boot (Master Spec
 * §42's BOOT step, before LOGIN), not an error condition the caller
 * needs a stack trace for.
 */
async function requestSessionRefresh(): Promise<AuthResponse | null> {
  try {
    const result = await apiRequest("/auth/refresh", { method: "POST" }, AuthResponseSchema);
    // A different tab may have replaced the shared refresh cookie. Never send
    // this account's offline queue under that other account's access token.
    if(sessionUserId&&result.user.id!==sessionUserId){setAccessToken(null);return null;}
    sessionUserId=result.user.id;
    setAccessToken(result.accessToken);
    return result;
  } catch {
    setAccessToken(null);
    return null;
  }
}

// Cookie rotation must have only one in-flight request per client.
let refreshing: Promise<AuthResponse | null> | null = null;
export function refreshSession(): Promise<AuthResponse | null> {
  if (!refreshing) refreshing = requestSessionRefresh().finally(() => { refreshing = null; });
  return refreshing;
}

export async function fetchCurrentUser(): Promise<UserDTO> {
  return apiRequest("/users/me", { method: "GET" }, UserDTOSchema);
}

/**
 * Wraps one authenticated API call with a single refresh-and-retry: if
 * `fn` throws an `ApiClientError` with status 401 (the access token
 * expired mid-session — its 15-minute default TTL, `JWT_ACCESS_TTL` from
 * Phase 09, is shorter than a long play session), this calls
 * `refreshSession()` once and retries `fn` exactly once more. A second
 * 401 (refresh itself failed — the refresh cookie is gone or expired
 * too) propagates to the caller rather than looping — the caller (see
 * `syncClient.ts`) is responsible for treating that as "session ended,"
 * not retrying forever.
 */
export async function withAuthRetry<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof ApiClientError && error.status === 401) {
      const refreshed = await refreshSession();
      if (refreshed) {
        return fn();
      }
    }
    throw error;
  }
}

export async function updateProfile(displayName:string):Promise<UserDTO>{
 return withAuthRetry(()=>apiRequest('/users/me',{method:'POST',body:{displayName}},UserDTOSchema));
}

export function forgetSession(){setAccessToken(null);sessionUserId=null;}
